import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { t } from '../../i18n';

import s from './MapFilter.module.scss';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import HoldingsList from '../HoldingsList/HoldingsList';
import { AnimateInOut } from '../Animate/Animate';
import Popover from '../Popover/Popover';
import Dropdown from '../Dropdown/Dropdown';
import MultiRangeSlider from '../MultiRangeSlider/MultiRangeSlider';
import Checkbox from '../Checkbox/Checkbox';
import OverviewSelect from '../OverviewSelect/OverviewSelect';

const MapFilter = ({
    holdings = [],
    activeHoldingId,
    setActiveHolding,
    country,
    allHoldingsLoaded = false,
    isLoading = false,
    isListView,
    onToggleView,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const itemRefs = useRef({});

    const categoryTypes = useMemo(() => {
        return holdings.reduce((acc, item, index) => {
            if (!acc.some((type) => type.value === item.category)) {
                acc.push({
                    id: index,
                    value: item.category,
                    label:
                        item?.category === 'Holding'
                            ? 'Holdings'
                            : item?.category?.charAt(0).toUpperCase() +
                              item?.category?.slice(1),
                });
            }
            return acc;
        }, []);
    }, [holdings]);

    const [selectedCategories, setSelectedCategories] = useState(
        categoryTypes.reduce((acc, item) => {
            if (item.value?.toLowerCase() === 'holding') {
                acc[item.value] = true;
            } else {
                acc[item.value] = false;
            }
            return acc;
        }, {})
    );

    const guoTypes = useMemo(() => {
        const selectedCategoriesValues = Object.keys(selectedCategories).filter(
            (key) => selectedCategories[key]
        );

        return holdings
            .filter((item) => {
                if (selectedCategoriesValues.length === 0) return true;
                return selectedCategoriesValues.includes(item.category);
            })
            .reduce((acc, item, index) => {
                if (
                    item.globalUltimateOwner &&
                    !acc.some((type) => type.value === item.globalUltimateOwner)
                ) {
                    acc.push({
                        id: index,
                        value: item.globalUltimateOwner,
                        label: item.globalUltimateOwner
                            ? item.globalUltimateOwner.charAt(0).toUpperCase() +
                              item.globalUltimateOwner.slice(1)
                            : '',
                    });
                }
                return acc;
            }, [])
            .sort((a, b) => a.value.localeCompare(b.value));
    }, [holdings, selectedCategories]);

    const [selectedGlobalUltimateOwner, setSelectedGlobalUltimateOwner] =
        useState(
            guoTypes.reduce((acc, item) => {
                acc[item.value] = false;
                return acc;
            }, {})
        );

    const { highestRevenue, highestEmployee } = useMemo(() => {
        if (holdings.length === 0) {
            return { highestRevenue: 0, highestEmployee: 0 };
        }

        return {
            highestRevenue: Math.max(
                ...holdings.map((item) => parseInt(item.revenue) || 0)
            ),
            highestEmployee: Math.max(
                ...holdings.map((item) => parseInt(item.employeeCount) || 0)
            ),
        };
    }, [holdings]);

    const [revenueMinVal, setRevenueMinVal] = useState(0);
    const [revenueMaxVal, setRevenueMaxVal] = useState(highestRevenue);

    const [employeesMinVal, setEmployeesMinVal] = useState(0);
    const [employeesMaxVal, setEmployeesMaxVal] = useState(highestEmployee);

    const handleRevenueRangeChange = ({ min, max }) => {
        setRevenueMinVal(min);
        setRevenueMaxVal(max);
    };

    useEffect(() => {
        setRevenueMaxVal(highestRevenue);
    }, [highestRevenue]);

    useEffect(() => {
        setEmployeesMaxVal(highestEmployee);
    }, [highestEmployee]);

    const handleEmployeesRangeChange = ({ min, max }) => {
        setEmployeesMinVal(min);
        setEmployeesMaxVal(max);
    };

    const popoverRef = useRef(null);

    const handleToggleView = () => {
        setIsListView((prevState) => !prevState);
    };

    const toggleFilterDropdown = () => {
        setIsFilterDropdownOpen(!isFilterDropdownOpen);
    };

    const handleClickOutside = (event) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target)) {
            setIsFilterDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCategoryChange = (value) => {
        setSelectedCategories((prevState) => ({
            ...prevState,
            [value]: !prevState[value],
        }));
    };

    const handleGlobalUltimateOwnerChange = (value) => {
        setSelectedGlobalUltimateOwner((prevState) => ({
            ...prevState,
            [value]: !prevState[value],
        }));
    };

    const filteredHoldings = useMemo(() => {
        return holdings.filter((holding) => {
            const matchesCountry =
                holding.countryCode === country.toUpperCase();
            const matchesSearchTerm = holding.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase());
            const matchesCategory = Object.keys(selectedCategories).some(
                (category) =>
                    selectedCategories[category] &&
                    holding.category === category
            );
            const matchesGlobalUltimateOwner = Object.keys(
                selectedGlobalUltimateOwner
            ).some(
                (guo) =>
                    selectedGlobalUltimateOwner[guo] &&
                    holding.globalUltimateOwner === guo
            );
            const isRevenueFilterActive =
                revenueMinVal > 0 || revenueMaxVal < highestRevenue;
            const isEmployeesFilterActive =
                employeesMinVal > 0 || employeesMaxVal < highestEmployee;
            const matchesRevenueRange =
                !isRevenueFilterActive ||
                (parseInt(holding.revenue) >= revenueMinVal &&
                    parseInt(holding.revenue) <= revenueMaxVal);
            const matchesEmployeesRange =
                !isEmployeesFilterActive ||
                (parseInt(holding.employeeCount) >= employeesMinVal &&
                    parseInt(holding.employeeCount) <= employeesMaxVal);

            return (
                matchesCountry &&
                matchesSearchTerm &&
                (matchesCategory ||
                    Object.values(selectedCategories).every((val) => !val)) &&
                (matchesGlobalUltimateOwner ||
                    Object.values(selectedGlobalUltimateOwner).every(
                        (val) => !val
                    )) &&
                matchesRevenueRange &&
                matchesEmployeesRange
            );
        });
    }, [
        holdings,
        searchTerm,
        revenueMinVal,
        revenueMaxVal,
        highestRevenue,
        employeesMinVal,
        employeesMaxVal,
        highestEmployee,
        country,
        selectedCategories,
        selectedGlobalUltimateOwner,
    ]);

    const activeFilters = useMemo(() => {
        let count = 0;

        if (searchTerm.length > 0) count++;
        count += Object.values(selectedCategories).filter(Boolean).length;
        count += Object.values(selectedGlobalUltimateOwner).filter(
            Boolean
        ).length;

        if (revenueMinVal > 0 || revenueMaxVal < highestRevenue) count++;
        if (employeesMinVal > 0 || employeesMaxVal < highestEmployee) count++;

        return count;
    }, [
        searchTerm,
        selectedCategories,
        selectedGlobalUltimateOwner,
        revenueMinVal,
        revenueMaxVal,
        highestRevenue,
        employeesMinVal,
        employeesMaxVal,
        highestEmployee,
    ]);

    const overviewSelectOptions = useMemo(() => {
        return filteredHoldings.map((holding) => ({
            value: holding.sourceId,
            label: holding.name,
        }));
    }, [filteredHoldings]);

    const handleOverviewSelectChange = (selectedOption) => {
        if (selectedOption) {
            const selectedHolding = holdings.find(
                (holding) =>
                    holding.sourceId?.toString() === selectedOption.value
            );
            setActiveHolding(selectedHolding.sourceId);

            // this fixes an issue with a white space appearing on the right side of
            // the map when selecting a holding
            // this is probably due to a race condition in mapbox as the map container
            // is resized when a holding is selected
            window.innerWidth += 1;
            setTimeout(() => {
                window.innerWidth -= 1;
                window.dispatchEvent(new Event('resize'));
            }, 10);

            if (itemRefs.current[selectedHolding.sourceId]) {
                itemRefs.current[selectedHolding.sourceId].scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        } else {
            setActiveHolding(null);

            const params = new URLSearchParams(window.location.search);
            params.delete('id');
            window.history.pushState(
                {},
                '',
                `${window.location.pathname}?${params}`
            );
        }
    };

    const classes = classNames(s.Root, { [s['Root--ListView']]: isListView });

    const onClearAllFilters = () => {
        setRevenueMinVal(0);
        setRevenueMaxVal(highestRevenue);
        setEmployeesMinVal(0);
        setEmployeesMaxVal(highestEmployee);
        setSelectedGlobalUltimateOwner(
            guoTypes.reduce((acc, item) => {
                acc[item.value] = false;
                return acc;
            }, {})
        );
        setSelectedCategories(
            categoryTypes.reduce((acc, item) => {
                acc[item.value] = false;
                return acc;
            }, {})
        );
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSelectHolding = (id) => {
        setActiveHolding(id);

        const params = new URLSearchParams(window.location.search);
        params.set('id', id);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({}, '', newUrl);
    };

    return (
        <div className={classes}>
            <div className={s.Container}>
                <div className={s.Left}>
                    {isListView && (
                        <FilterDropdown
                            popoverRef={popoverRef}
                            isFilterDropdownOpen={isFilterDropdownOpen}
                            toggleFilterDropdown={toggleFilterDropdown}
                            revenueMinVal={revenueMinVal}
                            revenueMaxVal={revenueMaxVal}
                            revenueMax={highestRevenue}
                            onRevenueRangeChange={handleRevenueRangeChange}
                            employeesMinVal={employeesMinVal}
                            employeesMaxVal={employeesMaxVal}
                            employeesMax={highestEmployee}
                            onEmployeesRangeChange={handleEmployeesRangeChange}
                            showRevenueCheckmark={
                                revenueMinVal !== 0 ||
                                revenueMaxVal !== highestRevenue
                            }
                            showEmployeeCheckmark={
                                employeesMinVal !== 0 ||
                                employeesMaxVal !== highestEmployee
                            }
                            globalUltimateOwnerFilterItems={guoTypes}
                            selectedGlobalUltimateOwnerFilter={
                                selectedGlobalUltimateOwner
                            }
                            handleGlobalUltimateOwnerChange={
                                handleGlobalUltimateOwnerChange
                            }
                            categoryFilterItems={categoryTypes}
                            selectedCategoryFilterItems={selectedCategories}
                            handleCategoryChange={handleCategoryChange}
                            onClearAllFilters={onClearAllFilters}
                            activeFilters={activeFilters}
                        />
                    )}
                    <div className={s.MobileOnly}>
                        <div className={s.ButtonToggle}>
                            <Button
                                title={t('mapFilter.viewListButton')}
                                modifier="Toggle"
                                size="Large"
                                isActive={isListView}
                                onClick={onToggleView}
                            />
                            <Button
                                title={t('mapFilter.mapButton')}
                                modifier="Toggle"
                                size="Large"
                                isActive={!isListView}
                                onClick={onToggleView}
                            />
                        </div>
                    </div>
                    <div className={s.Search}>
                        {isListView ? (
                            <div
                                className={classNames(s.SearchInputContainer, {
                                    [s['SearchInputContainer--Active']]:
                                        searchTerm.length > 0,
                                })}>
                                <Icon
                                    name="Search"
                                    className={s.SearchIcon}
                                    size="Small"
                                />
                                <input
                                    type="text"
                                    placeholder={t('mapFilter.searchHoldings')}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className={s.SearchInput}
                                />
                            </div>
                        ) : (
                            <OverviewSelect
                                placeholder={t('mapFilter.searchHoldings')}
                                value={activeHoldingId}
                                options={overviewSelectOptions}
                                handleChange={handleOverviewSelectChange}
                                modifier="WhiteBackground"
                                isClearable
                            />
                        )}
                    </div>
                </div>
                <div className={s.DesktopOnly}>
                    <div className={s.ButtonToggle}>
                        <Button
                            title={t('mapFilter.viewListButton')}
                            modifier="Toggle"
                            size="Large"
                            isActive={isListView}
                            onClick={onToggleView}
                        />
                        <Button
                            title={t('mapFilter.mapButton')}
                            modifier="Toggle"
                            size="Large"
                            isActive={!isListView}
                            onClick={onToggleView}
                        />
                    </div>
                </div>
            </div>
            <AnimateInOut
                isVisible={isListView}
                className={s.HoldingsList}
                duration={0}>
                <HoldingsList
                    isListView={isListView}
                    holdings={filteredHoldings}
                    activeHoldingId={activeHoldingId}
                    setActiveHolding={handleSelectHolding}
                    itemRefs={itemRefs}
                    allHoldingsLoaded={allHoldingsLoaded}
                    isLoading={isLoading}
                />

                <div className={s.Fade} />
            </AnimateInOut>
        </div>
    );
};

const FilterDropdown = React.memo(
    ({
        popoverRef,
        isFilterDropdownOpen,
        toggleFilterDropdown,
        revenueMinVal,
        revenueMaxVal,
        onRevenueRangeChange,
        employeesMinVal,
        employeesMaxVal,
        onEmployeesRangeChange,
        showRevenueCheckmark,
        showEmployeeCheckmark,
        revenueMax,
        employeesMax,
        globalUltimateOwnerFilterItems = [],
        selectedGlobalUltimateOwnerFilter = {},
        handleGlobalUltimateOwnerChange,
        categoryFilterItems = [],
        selectedCategoryFilterItems = {},
        handleCategoryChange,
        onClearAllFilters,
        activeFilters,
    }) => {
        const [globalUltimateOwnerSearch, setGlobalUltimateOwnerSearch] =
            useState('');
        const isFilterApplied = useMemo(() => {
            return (
                (categoryFilterItems.length > 1 &&
                    Object.values(selectedCategoryFilterItems).some(
                        (val) => val
                    )) ||
                Object.values(selectedGlobalUltimateOwnerFilter).some(
                    (val) => val
                ) ||
                revenueMinVal !== 0 ||
                revenueMaxVal !== revenueMax ||
                employeesMinVal !== 0 ||
                employeesMaxVal !== employeesMax
            );
        }, [
            selectedCategoryFilterItems,
            selectedGlobalUltimateOwnerFilter,
            revenueMinVal,
            revenueMaxVal,
            revenueMax,
            employeesMinVal,
            employeesMaxVal,
            employeesMax,
            categoryFilterItems,
        ]);

        const filteredGlobalUltimateOwnerItems =
            globalUltimateOwnerFilterItems.filter((item) =>
                item.label
                    .toLowerCase()
                    .includes(globalUltimateOwnerSearch.toLowerCase())
            );

        return (
            <Popover
                popoverRef={popoverRef}
                isPopoverOpen={isFilterDropdownOpen}
                minWidth="233px"
                popoverWidth="fit-content"
                disableLayoutAnimation
                modifier="FullWidth"
                onClick={toggleFilterDropdown}
                overrideButton={
                    <div className={s.Filter}>
                        <Button
                            title={isFilterDropdownOpen ? 'Close' : 'Filter'}
                            modifier="FilterAlternative"
                            size="Small"
                            icon={
                                isFilterDropdownOpen ? 'FilterWhite' : 'Filter'
                            }
                            isActive={isFilterDropdownOpen}
                            description={
                                activeFilters > 0 && `(${activeFilters})`
                            }
                        />
                    </div>
                }
                content={
                    <div className={s.PopoverContent}>
                        <Dropdown
                            modifiers={['BorderBottom']}
                            title={t('mapFilter.revenue')}
                            showCheckmark={showRevenueCheckmark}>
                            <div className={s.DropdownContent}>
                                <MultiRangeSlider
                                    min={0}
                                    max={revenueMax}
                                    minVal={revenueMinVal}
                                    maxVal={revenueMaxVal}
                                    onChange={onRevenueRangeChange}
                                    valueText=" USD"
                                    formatValues
                                />
                            </div>
                        </Dropdown>
                        <Dropdown
                            modifiers={['BorderBottom']}
                            title={t('mapFilter.numberOfEmployees')}
                            showCheckmark={showEmployeeCheckmark}>
                            <div className={s.DropdownContent}>
                                <MultiRangeSlider
                                    min={0}
                                    max={employeesMax}
                                    minVal={employeesMinVal}
                                    maxVal={employeesMaxVal}
                                    onChange={onEmployeesRangeChange}
                                    valueText=" employees"
                                />
                            </div>
                        </Dropdown>

                        {categoryFilterItems.length > 1 && (
                            <Dropdown
                                modifiers={['BorderBottom']}
                                title={t('mapFilter.category')}
                                showCheckmark={Object.values(
                                    selectedCategoryFilterItems
                                ).some((val) => val)}>
                                <ul className={s.CheckboxList}>
                                    {categoryFilterItems.map((items) => (
                                        <li
                                            key={items.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {items.label}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={items.id}
                                                checked={
                                                    selectedCategoryFilterItems[
                                                        items.value
                                                    ]
                                                }
                                                onChange={() =>
                                                    handleCategoryChange(
                                                        items.value
                                                    )
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                        )}

                        <Dropdown
                            modifiers={['BorderBottom']}
                            title={t('mapFilter.globalUltimateOwner')}
                            showCheckmark={Object.values(
                                selectedGlobalUltimateOwnerFilter
                            ).some((val) => val)}>
                            <div className={s.DropdownSearchWrapper}>
                                <Icon
                                    name="Search"
                                    className={s.DropdownSearchIcon}
                                    size="Small"
                                />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={globalUltimateOwnerSearch}
                                    onChange={(e) =>
                                        setGlobalUltimateOwnerSearch(
                                            e.target.value
                                        )
                                    }
                                    className={s.DropdownSearch}
                                />
                                {globalUltimateOwnerSearch.length > 0 && (
                                    <button
                                        className={s.DropdownClear}
                                        onClick={() =>
                                            setGlobalUltimateOwnerSearch('')
                                        }>
                                        <Icon
                                            name="Close"
                                            className={s.DropdownClearIcon}
                                            size="Small"
                                        />
                                    </button>
                                )}
                            </div>
                            <ul className={s.CheckboxListScroll}>
                                {filteredGlobalUltimateOwnerItems.map(
                                    (items) => (
                                        <li
                                            key={items.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {items.label}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={items.id}
                                                checked={
                                                    selectedGlobalUltimateOwnerFilter[
                                                        items.value
                                                    ]
                                                }
                                                onChange={() =>
                                                    handleGlobalUltimateOwnerChange(
                                                        items.value
                                                    )
                                                }
                                            />
                                        </li>
                                    )
                                )}
                            </ul>
                        </Dropdown>

                        {isFilterApplied && (
                            <div className={s.FilterButtons}>
                                <button
                                    className={s.ClearAllFilters}
                                    onClick={onClearAllFilters}>
                                    {t('mapFilter.clear')}
                                    <Icon
                                        name="Close"
                                        className={s.FilterIcon}
                                    />
                                </button>
                                <div className={s.FilterSeparator} />
                                <button
                                    className={s.ClearAllFilters}
                                    onClick={toggleFilterDropdown}>
                                    {t('mapFilter.done')}
                                </button>
                            </div>
                        )}
                    </div>
                }></Popover>
        );
    }
);

FilterDropdown.propTypes = {
    popoverRef: PropTypes.object.isRequired,
    isFilterDropdownOpen: PropTypes.bool.isRequired,
    toggleFilterDropdown: PropTypes.func.isRequired,
    revenueMinVal: PropTypes.number.isRequired,
    revenueMaxVal: PropTypes.number.isRequired,
    onRevenueRangeChange: PropTypes.func.isRequired,
    employeesMinVal: PropTypes.number.isRequired,
    employeesMaxVal: PropTypes.number.isRequired,
    onEmployeesRangeChange: PropTypes.func.isRequired,
    showRevenueCheckmark: PropTypes.bool.isRequired,
    showEmployeeCheckmark: PropTypes.bool.isRequired,
    revenueMax: PropTypes.number.isRequired,
    employeesMax: PropTypes.number.isRequired,
};

export default MapFilter;
